import type { History, Stroke } from './types';

/**
 * Taille maximum de l'historique (en nombre de strokes).
 * Au-delà, on supprime les strokes les plus anciens.
 *
 * 100 strokes ≈ ~100 éditions LIFO. Suffisant pour l'usage typique d'un éditeur
 * de patron de perles. Augmentable si besoin.
 */
export const DEFAULT_HISTORY_LIMIT = 100;

/**
 * Ajoute un stroke en fin de `past`. Vide `future` (les redos sont perdus).
 * - Ignore les strokes vides (no-op).
 * - Tronque `past` à `limit` strokes les plus récents.
 */
export function push(history: History, stroke: Stroke, limit = DEFAULT_HISTORY_LIMIT): History {
  if (stroke.length === 0) return history;
  let past = [...history.past, stroke];
  if (past.length > limit) past = past.slice(past.length - limit);
  return { past, future: [] };
}

export function canUndo(history: History): boolean {
  return history.past.length > 0;
}

export function canRedo(history: History): boolean {
  return history.future.length > 0;
}

/**
 * Retire le dernier stroke de `past`, le pousse en tête de `future`.
 * Retourne le stroke à reverter, ou `null` si rien à défaire.
 */
export function undo(history: History): { stroke: Stroke | null; history: History } {
  const last = history.past[history.past.length - 1];
  if (last === undefined) return { stroke: null, history };
  return {
    stroke: last,
    history: {
      past: history.past.slice(0, -1),
      future: [last, ...history.future],
    },
  };
}

/**
 * Retire le premier stroke de `future`, le pousse en fin de `past`.
 * Retourne le stroke à réappliquer, ou `null` si rien à refaire.
 */
export function redo(history: History): { stroke: Stroke | null; history: History } {
  const next = history.future[0];
  if (next === undefined) return { stroke: null, history };
  return {
    stroke: next,
    history: {
      past: [...history.past, next],
      future: history.future.slice(1),
    },
  };
}
